import './index.css'
import '../button/index.css';
import StatusPill from '../status-pill';
import { useState, useEffect } from 'react';

function formatAiGeneratedPercent(score) {
  if (score === null || score === undefined || score === '') return null;
  const n = typeof score === 'string' ? parseFloat(score) : Number(score);
  if (!Number.isFinite(n)) return null;
  if (n >= 0 && n <= 1) return Math.round(n * 100);
  return Math.round(Math.min(100, Math.max(0, n)));
}

function FeedItem({
  date,
  tag,
  site,
  url,
  title,
  expand,
  index,
  hasBeenSeen,
  display,
  desc,
  insight,
  image,
  placeholder,
  wordCount,
  targetKeyword,
  aiGeneratedScore,
}) {
  const [logo, setLogo] = useState('./images/fallback-logo.png');
  const [displayedFeedImage, setDisplayedFeedImage] = useState(null); // null = hidden / loading

  // Fetch logo only when `url` changes
  useEffect(() => {
    if (!url) {
      setLogo('./images/fallback-logo.png');
      return;
    }

    let cancelled = false;

    const getLogoUrl = async () => {
      const token = process.env.REACT_APP_LOGO_DEV_TOKEN;
      if (!token) {
        if (!cancelled) setLogo('./images/fallback-logo.png');
        return;
      }
      try {
        const domain = new URL(url).hostname;
        const call = `https://img.logo.dev/${domain}?token=${token}`;
        const result = await fetch(call);

        if (!cancelled) {
          if (result.ok) {
            setLogo(call);
          } else {
            setLogo('./images/fallback-logo.png');
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLogo('./images/fallback-logo.png');
        }
      }
    };

    getLogoUrl();

    return () => {
      cancelled = true;
    };
  }, [url]);

  const hasInsight = typeof insight === "string" && insight.trim().length > 0;
  const displayedExcerpt = (hasInsight ? insight : desc) || "No summary available.";

  const aiPercent = formatAiGeneratedPercent(aiGeneratedScore);
  const keywordLabel =
    typeof targetKeyword === "string" && targetKeyword.trim()
      ? targetKeyword.trim()
      : null;
  const wordCountNum =
    wordCount === null || wordCount === undefined || wordCount === ""
      ? null
      : Number(wordCount);
  const hasWordCount = Number.isFinite(wordCountNum) && wordCountNum >= 0;
  const metaParts = [];
  if (aiPercent !== null) {
    metaParts.push({
      key: "ai",
      label: "AI probability",
      text: `${aiPercent}%`,
      title:
        "Estimated probability that this content was produced by AI rather than written entirely by a person.",
    });
  }
  if (keywordLabel) {
    metaParts.push({
      key: "kw",
      label: "Keyword",
      text: keywordLabel,
      title: "Target keyword",
    });
  }
  if (hasWordCount) {
    metaParts.push({
      key: "wc",
      label: "Words",
      text: wordCountNum.toLocaleString(),
      title: "Word count",
    });
  }

  // Fetch image only when `image` changes & hide it while loading
  useEffect(() => {
    if (!image) {
      setDisplayedFeedImage(null);
      return;
    }

    let cancelled = false;

    const getImage = async () => {
      // hide while loading
      setDisplayedFeedImage(null);

      try {
        const call = image;
        const result = await fetch(call, { method: 'HEAD' }); 
        // HEAD is often enough just to check if it exists

        if (!cancelled && result.ok) {
          setDisplayedFeedImage(call);
        }
      } catch (error) {
        if (!cancelled) {
          // stay hidden if it fails
          setDisplayedFeedImage(null);
        }
      }
    };

    getImage();

    return () => {
      cancelled = true;
    };
  }, [image]);

  const keywordMetaPart = metaParts.find((p) => p.key === 'kw') ?? null;
  const metricMetaParts = metaParts.filter((p) => p.key !== 'kw');
  const metricsGridColumns =
    metricMetaParts.length > 0
      ? metricMetaParts.map(() => 'minmax(0, 1fr)').join(' ')
      : undefined;

  const metaStrip =
    metaParts.length > 0 ? (
      <div
        className={
          'feed-meta-strip' +
          (hasInsight ? ' feed-meta-strip--in-panel' : ' feed-meta-strip--solo')
        }
        aria-label='Article metadata'
      >
        {keywordMetaPart ? (
          <div className='feed-meta-strip-row feed-meta-strip-row--keyword'>
            <div className='feed-meta-strip-cell feed-meta-strip-cell--keyword feed-meta-strip-cell--full'>
              <span className='feed-meta-strip-label'>{keywordMetaPart.label}</span>
              <span className='feed-meta-strip-value' title={keywordMetaPart.title}>
                {keywordMetaPart.text}
              </span>
            </div>
          </div>
        ) : null}
        {metricMetaParts.length > 0 ? (
          <div
            className='feed-meta-strip-row feed-meta-strip-row--metrics'
            style={{ gridTemplateColumns: metricsGridColumns }}
          >
            {metricMetaParts.map((part) => (
              <div key={part.key} className='feed-meta-strip-cell'>
                <span className='feed-meta-strip-label'>{part.label}</span>
                <span className='feed-meta-strip-value' title={part.title}>
                  {part.text}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    ) : null;

  return (
    <div className='feed-item-border' style={{ display: 'flex' }}>
      <div className='feed-item new-post'>
        <div
          className='placeholder-tag tiny-text'
          style={{ display: placeholder ? 'block' : 'none' }}
        >
          This is an example feed item.
        </div>

        <div className='date-and-tag-holder tiny-text'>
          <img className='feed-logo' src={logo} alt='logo' />
          <div className='feed-tag'>
            <div
              style={{ display: hasBeenSeen ? 'none' : 'block' }}
              className='new-content-globule'
            />
            <div>{date}</div>
          </div>
        </div>

        {/* Only show image once we’ve confirmed it’s okay */}
        <img
          src={displayedFeedImage || ''}
          style={{ display: displayedFeedImage ? 'block' : 'none' }}
          alt=''
        />

        <h2 className='medium-text'>{title}</h2>

        <div className='text-holder'>
          <a href={url} target='_blank' rel='noreferrer'>
            <p className='url tiny-text feed-item-url-truncate'>{url}</p>
          </a>

          <>
            {metaStrip}
            <div className='feed-insight-block'>
              <StatusPill
                colour='#0AFB60'
                text={hasInsight ? 'Insight from page' : 'Meta description'}
                active={hasInsight}
              />
              <p className='tiny-text overview-excerpt'>{displayedExcerpt}</p>
            </div>
          </>
        </div>

        <div className='action-bar'>
          {url ? (
            <a
              href={url}
              target='_blank'
              rel='noreferrer'
              className='button tiny-text'
            >
              <div className='button-contents'>
                <div>Read post</div>
              </div>
            </a>
          ) : (
            <span
              className='button tiny-text'
              style={{ opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' }}
              aria-disabled
            >
              <div className='button-contents'>
                <div>Read post</div>
              </div>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default FeedItem;
